import { v4 } from 'uuid';
import typo from "../typography.module.css";
import styles from "./SurveyEditor.module.css";

import {
    BlockTypeSelect,
    BoldItalicUnderlineToggles,
    CreateLink,
    diffSourcePlugin,
    DiffSourceToggleWrapper,
    headingsPlugin,
    linkDialogPlugin,
    listsPlugin,
    ListsToggle,
    MDXEditor,
    quotePlugin,
    Separator,
    tablePlugin,
    thematicBreakPlugin,
    toolbarPlugin,
    UndoRedo
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import {
    addEdge,
    Background,
    Controls,
    ReactFlow,
    ReactFlowProvider,
    useEdgesState,
    useNodesState,
    useReactFlow,
} from '@xyflow/react';

import '@xyflow/react/dist/style.css';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api";
import Button from "../components/Button";
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout";
import Select from "../components/Select";
import SurveyNode from "../components/SurveyNode";
import TextInput from "../components/TextInput";
import { isNullOrEmpty } from '../utils';
import { ButtonStatus } from '../common/constants';

const SurveyEditor = () => {

    const navigate = useNavigate();
    const { survey_id } = useParams();
    const [survey, setSurvey] = useState(null);
    const [newSurvey, setNewSurvey] = useState(null);
    const [saving, setSaving] = useState(false);
    const [buttonStatus, setButtonStatus] = useState(null);

    useEffect(() => {
        if(buttonStatus === ButtonStatus.Success){
            setTimeout(() => {
                setButtonStatus(null);
            }, 2000)
        }
    },[buttonStatus])

    useEffect(() => {
        const getSurvey = async () => {
            try {
                let survey = await api.get(`/admin/surveys/${survey_id}`);
                setSurvey(survey);
                setNewSurvey({ ...survey })
            }
            catch (e) {
                console.error(e)
            }
        };

        if (survey_id !== 'create') {
            getSurvey();
        }
        else {
            setSurvey({
                title: 'Nuovo questionario',
                description: '',
                message: '',
                status: 'draft',
                content: [],
            })
            setNewSurvey({
                title: 'Nuovo questionario',
                description: '',
                message: '',
                status: 'draft',
                content: [],
            })
        }
    }, [survey_id]);

    const onSurveyContentChange = (content) => {
        setNewSurvey((prev) => {
            prev.content = content
            return { ...prev }
        })
    }

    const statusOptions = useMemo(() => ([
        { id: 0, name: 'Bozza', value: 'draft' },
        { id: 1, name: 'Pubblico', value: 'public' },
        { id: 2, name: 'Nascosto', value: 'hidden' }
    ]), [])

    const save = async () => {
        setSaving(true);
        if (survey_id === 'create') {
            try {
                const s = await api.post(`/admin/surveys`, newSurvey);
                navigate(`/surveys/${s.id}/editor`)
            } catch (error) {
                console.error(error);
            }
        }
        else {
            try {
                await api.put(`/admin/surveys/${survey_id}`, newSurvey);
                setButtonStatus(ButtonStatus.Success);
            } catch (error) {
                console.error(error);
            }
        }
        setSaving(false);
    }

    const saveDisabled = useMemo(() => {
        if (!newSurvey || !survey) return true;
        if (JSON.stringify(newSurvey) === JSON.stringify(survey)) return true;
        if (isNullOrEmpty(newSurvey.title) ||
            isNullOrEmpty(newSurvey.description) ||
            isNullOrEmpty(newSurvey.message) || isNullOrEmpty(newSurvey.status) || saving) return true;
        return false;
    }, [newSurvey, survey, saving]);


    return (
        <HeaderFooterLayout>
            <div className={styles.container}>

                <ReactFlowProvider>
                    {survey &&
                        <SurveyFlowEditor survey={survey} onChange={onSurveyContentChange} />
                    }
                </ReactFlowProvider>
                <div className={styles.toolbar}>
                    {newSurvey &&

                        <div className={styles.infoContent}>
                            <div className={typo.headline}>
                                {newSurvey.content.length} domande
                            </div>
                            <div className={typo.caption}>STATO</div>
                            <Select selected={statusOptions.find(s => s.value === newSurvey?.status)} options={statusOptions} onSelect={(value) => {
                                setNewSurvey((prev) => {
                                    prev.status = value.value
                                    return { ...prev }
                                })
                            }} />
                            <div className={typo.caption}>TITOLO</div>
                            <TextInput type="text" placeholder="Titolo" value={newSurvey ? newSurvey.title : ''} onKeyUp={(value) => setNewSurvey({ ...newSurvey, title: value })} />
                            <div className={typo.caption}>DESCRIZIONE</div>

                            <div className={styles.editor}>
                                <MDXEditor
                                    className={styles.markdown}
                                    placeholder="Descrizione del questionario..."
                                    markdown={newSurvey ? newSurvey.description : ''}
                                    onChange={(md) => { setNewSurvey({ ...newSurvey, description: md }) }}
                                    plugins={[
                                        headingsPlugin(),
                                        listsPlugin(),
                                        quotePlugin(),
                                        thematicBreakPlugin(),
                                        linkDialogPlugin(),
                                        diffSourcePlugin(),
                                        tablePlugin(),
                                        toolbarPlugin({
                                            toolbarContents: () => (
                                                <>
                                                    {' '}
                                                    <UndoRedo />
                                                    <Separator />
                                                    <BoldItalicUnderlineToggles />
                                                    <Separator />
                                                    <ListsToggle />
                                                    <CreateLink />
                                                    <Separator />
                                                    <BlockTypeSelect />
                                                    <DiffSourceToggleWrapper options={["rich-text", "source"]} />
                                                </>
                                            )
                                        })
                                    ]}
                                />
                            </div>
                            <div className={typo.caption}>MESSAGGIO FINALE</div>

                            <div className={styles.editor}>
                                <MDXEditor
                                    className={styles.markdown}
                                    placeholder="Messaggio di ringraziamento..."
                                    markdown={newSurvey ? newSurvey.message : ''}
                                    onChange={(md) => { setNewSurvey({ ...newSurvey, message: md }) }}
                                    plugins={[
                                        headingsPlugin(),
                                        listsPlugin(),
                                        quotePlugin(),
                                        thematicBreakPlugin(),
                                        linkDialogPlugin(),
                                        diffSourcePlugin(),
                                        tablePlugin(),
                                        toolbarPlugin({
                                            toolbarContents: () => (
                                                <>
                                                    {' '}
                                                    <UndoRedo />
                                                    <Separator />
                                                    <BoldItalicUnderlineToggles />
                                                    <Separator />
                                                    <ListsToggle />
                                                    <CreateLink />
                                                    <Separator />
                                                    <BlockTypeSelect />
                                                    <DiffSourceToggleWrapper options={["rich-text", "source"]} />
                                                </>
                                            )
                                        })
                                    ]}
                                />
                            </div>

                            <div className={styles.spacer} />
                            <Button style={{padding: '1rem'}} status={buttonStatus} disabled={saveDisabled} onClick={save} fullWidth>SALVA</Button>

                        </div>
                    }

                </div>
            </div>
        </HeaderFooterLayout>
    )

}


const SurveyFlowEditor = ({ survey, onChange }) => {
    const id = v4();
    const initialNodes = [
        {
            id: id,
            type: 'survey',
            data: { title: 'Domanda 1', questionType: 'Open', text: '', required: true, group: '', options: [], min: 1, max: 5, step: 1, minLabel: '', maxLabel: '', maxSelections: null, minSelections: null, onChange: undefined },
            position: { x: 0, y: 50 },
        },
    ];

    const getId = () => v4();
    const nodeOrigin = [0.5, 0];

    const reactFlowWrapper = useRef(null);

    const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const { screenToFlowPosition, getViewport, setViewport, setCenter } = useReactFlow();
    // History management for undo/redo
    const historyRef = useRef([]); // stack of past snapshots
    const redoRef = useRef([]); // stack of future snapshots
    const lastSnapshotRef = useRef(null); // last applied snapshot
    const applyingHistoryRef = useRef(false);
    const [histCount, setHistCount] = useState(0);
    const [redoCount, setRedoCount] = useState(0);
    const snapshotOf = useCallback((nArr, eArr) => ({
        nodes: JSON.parse(JSON.stringify(nArr)),
        edges: JSON.parse(JSON.stringify(eArr)),
    }), []);
    const snapshotsEqual = useCallback((a, b) => {
        if (!a || !b) return false;
        return JSON.stringify(a) === JSON.stringify(b);
    }, []);
    const getEdgeLabel = useCallback((params) => {
        const src = nodes.find((n) => n.id === params.source);
        if (!src) return undefined;
        const { data } = src;
        if (params.sourceHandle && data?.options?.length) {
            const opt = data.options.find((o) => `opt-${o.id}` === params.sourceHandle);
            if (opt) return opt.value;
        }
        return 'next';
    }, [nodes]);

    const onConnect = useCallback(
        (params) => {
            const label = getEdgeLabel(params);
            setEdges((eds) => addEdge({ ...params, label }, eds));
        },
        [setEdges, getEdgeLabel],
    );

    // allow node internals to update their own data
    const onChangeNodeData = useCallback((nodeId, patch) => {
        setNodes((prev) =>
            prev.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, ...patch } } : n))
        );
    }, [setNodes]);

    const nodeTypes = useMemo(() => ({
        survey: SurveyNode,
    }), []);

    const sectionEntries = useMemo(() => {
        const seen = new Set();
        const entries = [];
        nodes.forEach((node) => {
            const rawGroup = typeof node?.data?.group === 'string' ? node.data.group.trim() : '';
            const key = rawGroup.length > 0 ? rawGroup : '__default__';
            if (seen.has(key)) return;
            seen.add(key);
            entries.push({
                key,
                label: rawGroup.length > 0 ? rawGroup : 'Senza sezione',
                nodeId: node.id,
            });
        });
        return entries;
    }, [nodes]);

    // helper to compute the next question id (stable numeric id, not based on visual order)
    const getNextQuestionIdFromNodes = useCallback((nodesArr) => {
        let maxId = 0;
        for (const n of nodesArr) {
            const qid = Number(n?.data?.qid ?? 0);
            if (!Number.isNaN(qid)) maxId = Math.max(maxId, qid);
        }
        return maxId + 1;
    }, []);

    // Build a JSON structure resembling the survey schema based on nodes and edges
    const buildSurveyJSON = useCallback((nodesArr, edgesArr) => {
        // sort nodes by position for sequential fallback only; use stable qid for ids
        const sorted = [...nodesArr].sort((a, b) => (a.position.y - b.position.y) || (a.position.x - b.position.x));
        const idMap = new Map(nodesArr.map((n) => [n.id, Number(n?.data?.qid)]));

        const getNextByHandle = (sourceId, handleId) => {
            const e = edgesArr.find((ed) => ed.source === sourceId && ed.sourceHandle === handleId);
            if (!e) return undefined;
            return idMap.get(e.target) ?? null;
        };

        const getDefaultNext = (sourceId) => {
            // explicit default edge first
            const e = edgesArr.find((ed) => ed.source === sourceId && (!ed.sourceHandle || ed.sourceHandle === 'default'));
            if (e) return idMap.get(e.target) ?? null;
            // fallback to sequential order
            const idx = sorted.findIndex((n) => n.id === sourceId);
            if (idx >= 0 && idx + 1 < sorted.length) return idMap.get(sorted[idx + 1].id);
            return null;
        };

        // explicit default next only (no sequential fallback)
        const getExplicitDefaultNext = (sourceId) => {
            const e = edgesArr.find((ed) => ed.source === sourceId && (!ed.sourceHandle || ed.sourceHandle === 'default'));
            if (e) return idMap.get(e.target) ?? null;
            return undefined;
        };

        const survey = sorted.map((n) => {
            const d = n.data || {};
            const base = {
                id: Number(n?.data?.qid),
                text: d.text || '',
                type: d.questionType || 'Open',
                // always include required explicitly to reflect UI state
                required: (typeof d.required === 'boolean') ? d.required : true,
                group: (typeof d.group === 'string') ? d.group : '',
            };

            if (d.questionType === 'Numeric') {
                base.min = typeof d.min === 'number' ? d.min : 1;
                base.max = typeof d.max === 'number' ? d.max : 5;
                base.step = typeof d.step === 'number' ? d.step : 1;
                base.minLabel = typeof d.minLabel === 'string' ? d.minLabel : '';
                base.maxLabel = typeof d.maxLabel === 'string' ? d.maxLabel : '';
                base.next = getDefaultNext(n.id);
                return base;
            }

            if (d.questionType === 'SingleChoice' || d.questionType === 'MultipleChoice') {
                const opts = Array.isArray(d.options) ? d.options : [];
                const defaultNext = getDefaultNext(n.id);
                const explicitDefault = getExplicitDefaultNext(n.id);
                base.options = opts.map((o) => {
                    const optIsOpen = typeof o.type === 'string' && o.type.toLowerCase() === 'open';
                    return {
                    value: o.value ?? '',
                        ...(optIsOpen ? { type: 'open' } : {}),
                    next: d.questionType === 'SingleChoice'
                        ? (() => {
                            const byHandle = getNextByHandle(n.id, `opt-${o.id}`);
                            // if option has its own edge, use it; else use explicit default if exists; else null
                            return (byHandle !== undefined) ? byHandle : (explicitDefault ?? null);
                        })()
                        : (explicitDefault ?? null),
                    };
                });
                const allOptionsHaveNext = d.questionType === 'SingleChoice'
                    && base.options.length > 0
                    && base.options.every((opt) => opt.next !== undefined && opt.next !== null);
                base.next = allOptionsHaveNext ? null : defaultNext; // drop fallback when every branch is explicit
                if (d.questionType === 'MultipleChoice') {
                    const limit = (typeof d.maxSelections === 'number' && !Number.isNaN(d.maxSelections)) ? d.maxSelections : null;
                    if (limit !== null) {
                        base.maxSelections = limit;
                    }
                    const minLimit = (typeof d.minSelections === 'number' && !Number.isNaN(d.minSelections)) ? d.minSelections : null;
                    if (minLimit !== null) {
                        base.minSelections = minLimit;
                    }
                }
                return base;
            }

            // Open or others
            if (d.questionType === 'Open' && typeof d.validation === 'string' && d.validation.trim().length > 0) {
                base.validation = d.validation.trim();
            }
            base.next = getDefaultNext(n.id);
            return base;
        });

        return survey;
    }, []);

    // Import a survey JSON array into nodes/edges
    const importSurvey = useCallback((surveyArr) => {
        if (!Array.isArray(surveyArr)) return;

        // If empty survey content, ensure at least one default question exists
        if (surveyArr.length === 0) {
            const nodeId = getId();
            const defaultNode = {
                id: nodeId,
                type: 'survey',
                position: { x: 0, y: 50 },
                data: {
                    title: 'Domanda 1',
                    text: '',
                    required: true,
                    group: '',
                    questionType: 'Open',
                    options: [],
                    min: 1,
                    max: 5,
                    step: 1,
                    minLabel: '',
                    maxLabel: '',
                    maxSelections: null,
                    minSelections: null,
                    validation: '',
                    onChange: onChangeNodeData,
                    qid: 1,
                },
                origin: [0.5, 0.0],
            };
            setNodes([defaultNode]);
            setEdges([]);
            // reset history to baseline
            const baseline = snapshotOf([defaultNode], []);
            lastSnapshotRef.current = baseline;
            historyRef.current = [];
            redoRef.current = [];
            setHistCount(0);
            setRedoCount(0);
            return;
        }

        // create nodes first
        const idMap = new Map(); // question id (number|string) -> nodeId (uuid)
        const optionMetaMap = new Map(); // nodeId -> { meta: [...], order: Map<handle, index> }
        const nodeHeightMap = new Map(); // nodeId -> estimated height for layout spacing

        // estimate node height to space them so they are fully visible
        const estimateHeight = (q, qType, optionsLen) => {
            let h = 240; // larger base height for title, text, controls
            if (qType === 'SingleChoice' || qType === 'MultipleChoice') {
                h += 40 + (optionsLen * 36); // per-option rows (more generous)
                if (qType === 'MultipleChoice') {
                    h += 70; // additional space for selection constraints
                }
            } else if (qType === 'Numeric') {
                h += 140; // numeric fields block plus labels
            }
            return h;
        };

        const GAP = 160; // reduced gap (half of previous)
        const MIN_STEP = 260; // reduced minimum step (half of previous)
        const newNodes = [];
        surveyArr.forEach((q, idx) => {
            const nodeId = getId();
            idMap.set(String(q.id ?? idx + 1), nodeId);
            const qType = q.type || 'Open';
            const optList = Array.isArray(q.options) ? q.options : [];
            const createdOptions = (qType === 'SingleChoice' || qType === 'MultipleChoice')
                ? optList.map((o) => {
                    const isOpen = typeof o.type === 'string' && o.type.toLowerCase() === 'open';
                    return { id: getId(), value: o.value ?? '', ...(isOpen ? { type: 'open' } : {}) };
                })
                : [];
            const optionMeta = createdOptions.map((o, i) => ({ idx: i, optId: o.id, value: o.value }));
            const handleOrder = new Map(optionMeta.map((meta) => [`opt-${meta.optId}`, meta.idx]));
            optionMetaMap.set(nodeId, { meta: optionMeta, order: handleOrder });

            // placeholder position; actual layout computed after edge mapping
            newNodes.push({
                id: nodeId,
                type: 'survey',
                position: { x: 0, y: 0 },
                data: {
                    title: `Domanda ${q.id ?? (idx + 1)}`,
                    text: q.text || '',
                    // When loading existing survey, if 'required' field is missing, default to true
                    required: (typeof q.required === 'boolean') ? q.required : true,
                    group: typeof q.group === 'string' ? q.group : '',
                    questionType: qType,
                    options: createdOptions,
                    min: typeof q.min === 'number' ? q.min : 1,
                    max: typeof q.max === 'number' ? q.max : 5,
                    step: typeof q.step === 'number' ? q.step : 1,
                    minLabel: typeof q.minLabel === 'string' ? q.minLabel : '',
                    maxLabel: typeof q.maxLabel === 'string' ? q.maxLabel : '',
                    maxSelections: typeof q.maxSelections === 'number' && !Number.isNaN(q.maxSelections)
                        ? q.maxSelections
                        : null,
                    minSelections: typeof q.minSelections === 'number' && !Number.isNaN(q.minSelections)
                        ? q.minSelections
                        : null,
                    validation: typeof q.validation === 'string' ? q.validation : '',
                    onChange: onChangeNodeData,
                    qid: Number(q.id ?? (idx + 1)),
                },
                origin: [0.5, 0.0],
            });

            const est = estimateHeight(q, qType, createdOptions.length);
            nodeHeightMap.set(nodeId, Math.max(est, MIN_STEP));
        });

        // then create edges
        const newEdges = [];
        surveyArr.forEach((q, idx) => {
            const sourceNodeId = idMap.get(String(q.id ?? (idx + 1)));
            if (!sourceNodeId) return;
            const qType = q.type || 'Open';

            // compute uniform option next (for SingleChoice): if all options have same non-null next
            let uniformOptionTargetId;
            if (qType === 'SingleChoice' && Array.isArray(q.options) && q.options.length > 0) {
                const nextVals = q.options.map((o) => o?.next);
                const allHaveNext = nextVals.every((v) => v !== undefined && v !== null);
                if (allHaveNext) {
                    const first = String(nextVals[0]);
                    const allSame = nextVals.every((v) => String(v) === first);
                    if (allSame) {
                        uniformOptionTargetId = idMap.get(first);
                    }
                }
            }

            // default next at question level
            let hasDefault = false;
            if (q.next !== undefined && q.next !== null) {
                const targetNodeId = idMap.get(String(q.next));
                if (targetNodeId) {
                    newEdges.push({
                        id: getId(),
                        source: sourceNodeId,
                        sourceHandle: 'default',
                        target: targetNodeId,
                        label: 'next',
                    });
                    hasDefault = true;
                }
            }

            // If uniform option next exists, connect question default directly and skip per-option edges
            if (uniformOptionTargetId) {
                if (!hasDefault) {
                    newEdges.push({
                        id: getId(),
                        source: sourceNodeId,
                        sourceHandle: 'default',
                        target: uniformOptionTargetId,
                        label: 'next',
                    });
                }
                return; // skip per-option edges
            }

            // per-option next (SingleChoice only, matches UI capability)
            if (qType === 'SingleChoice' && Array.isArray(q.options)) {
                const registryEntry = optionMetaMap.get(sourceNodeId);
                const optMeta = registryEntry?.meta || [];
                q.options.forEach((o, i) => {
                    if (o && o.next !== undefined && o.next !== null) {
                        const targetNodeId = idMap.get(String(o.next));
                        const meta = optMeta[i];
                        if (targetNodeId && meta) {
                            newEdges.push({
                                id: getId(),
                                source: sourceNodeId,
                                sourceHandle: `opt-${meta.optId}`,
                                target: targetNodeId,
                                label: meta.value || 'next',
                            });
                        }
                    }
                });
            }
        });

        const layoutImportedNodes = (nodesList, edgesList) => {
            if (!Array.isArray(nodesList) || nodesList.length === 0) return;

            const nodeById = new Map(nodesList.map((n) => [n.id, n]));
            const incomingCounts = new Map(nodesList.map((n) => [n.id, 0]));
            edgesList.forEach((edge) => {
                incomingCounts.set(edge.target, (incomingCounts.get(edge.target) ?? 0) + 1);
            });

            const roots = nodesList
                .filter((n) => (incomingCounts.get(n.id) ?? 0) === 0)
                .sort((a, b) => {
                    const qa = Number(a?.data?.qid ?? 0);
                    const qb = Number(b?.data?.qid ?? 0);
                    return qa - qb;
                });

            const layoutInfo = new Map();
            const queue = [];

            if (roots.length === 0) {
                const first = nodesList[0];
                if (first) {
                    layoutInfo.set(first.id, { depth: 0, column: 0 });
                    queue.push(first.id);
                }
            } else {
                roots.forEach((root, idx) => {
                    const initialColumn = idx * 2; // spread multiple roots apart
                    layoutInfo.set(root.id, { depth: 0, column: initialColumn });
                    queue.push(root.id);
                });
            }

            const edgesBySource = new Map();
            edgesList.forEach((edge) => {
                if (!edgesBySource.has(edge.source)) edgesBySource.set(edge.source, []);
                edgesBySource.get(edge.source).push(edge);
            });

            const assignLayout = (nodeId, column, depth) => {
                const existing = layoutInfo.get(nodeId);
                if (existing) {
                    if (depth < existing.depth || (depth === existing.depth && column !== existing.column)) {
                        layoutInfo.set(nodeId, { depth, column });
                        queue.push(nodeId);
                    }
                    return;
                }
                layoutInfo.set(nodeId, { depth, column });
                queue.push(nodeId);
            };

            const processQueue = () => {
                while (queue.length > 0) {
                    const currentId = queue.shift();
                    const info = layoutInfo.get(currentId);
                    if (!info) continue;

                    const outgoing = edgesBySource.get(currentId) || [];
                    if (outgoing.length === 0) continue;

                    const optionEdges = outgoing.filter((edge) => typeof edge.sourceHandle === 'string' && edge.sourceHandle.startsWith('opt-'));
                    const defaultEdges = outgoing.filter((edge) => !edge.sourceHandle || edge.sourceHandle === 'default');

                    if (optionEdges.length > 0) {
                        const handleOrder = optionMetaMap.get(currentId)?.order;
                        optionEdges.sort((a, b) => {
                            const idxA = handleOrder?.get(a.sourceHandle) ?? 0;
                            const idxB = handleOrder?.get(b.sourceHandle) ?? 0;
                            if (idxA !== idxB) return idxA - idxB;
                            if (a.target === b.target) return 0;
                            return a.target > b.target ? 1 : -1;
                        });

                        optionEdges.forEach((edge, index) => {
                            const offset = optionEdges.length === 1 ? 0 : (index - (optionEdges.length - 1) / 2);
                            const childColumn = info.column + offset;
                            assignLayout(edge.target, childColumn, info.depth + 1);
                        });
                    }

                    defaultEdges.forEach((edge) => {
                        assignLayout(edge.target, info.column, info.depth + 1);
                    });
                }
            };

            processQueue();

            const unassignedNodes = nodesList.filter((node) => !layoutInfo.has(node.id));
            if (unassignedNodes.length > 0) {
                const baseColumn = layoutInfo.size;
                unassignedNodes.forEach((node, idx) => {
                    const column = baseColumn + idx;
                    layoutInfo.set(node.id, { depth: 0, column });
                    queue.push(node.id);
                });
                processQueue();
            }

            const orderedEntries = Array.from(layoutInfo.entries()).sort((a, b) => {
                const depthDiff = a[1].depth - b[1].depth;
                if (depthDiff !== 0) return depthDiff;
                return a[1].column - b[1].column;
            });
            const occupiedByDepth = new Map();
            orderedEntries.forEach(([nodeId, info]) => {
                const depth = info.depth;
                const used = occupiedByDepth.get(depth) ?? new Set();
                let candidateColumn = info.column;
                while (used.has(candidateColumn)) {
                    candidateColumn += 1;
                }
                if (candidateColumn !== info.column) {
                    layoutInfo.set(nodeId, { depth, column: candidateColumn });
                }
                used.add(candidateColumn);
                occupiedByDepth.set(depth, used);
            });

            const groupOrder = new Map();
            const groupMinColumn = new Map();
            layoutInfo.forEach(({ column }, nodeId) => {
                const node = nodeById.get(nodeId);
                const groupKey = typeof node?.data?.group === 'string' && node.data.group.trim().length > 0
                    ? node.data.group.trim()
                    : '';
                if (!groupOrder.has(groupKey)) {
                    groupOrder.set(groupKey, groupOrder.size);
                }
                const currentMin = groupMinColumn.get(groupKey);
                if (currentMin === undefined || column < currentMin) {
                    groupMinColumn.set(groupKey, column);
                }
            });

            const groupColumnSpacing = 960; // ample distance between groups
            const branchColumnSpacing = 320; // spacing inside a group for different branches

            const depthHeights = new Map();
            layoutInfo.forEach(({ depth }, nodeId) => {
                const estHeight = nodeHeightMap.get(nodeId) ?? MIN_STEP;
                const currentMax = depthHeights.get(depth) ?? 0;
                depthHeights.set(depth, Math.max(currentMax, estHeight));
            });

            const depthOffsets = new Map();
            const sortedDepths = Array.from(depthHeights.keys()).sort((a, b) => a - b);
            sortedDepths.forEach((depth, idx) => {
                if (idx === 0) {
                    depthOffsets.set(depth, 0);
                    return;
                }
                const prevDepth = sortedDepths[idx - 1];
                const prevOffset = depthOffsets.get(prevDepth) ?? 0;
                const prevHeight = depthHeights.get(prevDepth) ?? MIN_STEP;
                depthOffsets.set(depth, prevOffset + prevHeight + GAP);
            });

            nodesList.forEach((node) => {
                const info = layoutInfo.get(node.id);
                if (!info) return;
                const groupKey = typeof node?.data?.group === 'string' && node.data.group.trim().length > 0
                    ? node.data.group.trim()
                    : '';
                const groupIdx = groupOrder.get(groupKey) ?? 0;
                const minCol = groupMinColumn.get(groupKey) ?? 0;
                const normalizedColumn = info.column - minCol;
                const x = (groupIdx * groupColumnSpacing) + (normalizedColumn * branchColumnSpacing);
                const y = depthOffsets.get(info.depth) ?? 0;
                node.position = { x, y };
            });
        };

        layoutImportedNodes(newNodes, newEdges);

        setNodes(newNodes);
        setEdges(newEdges);

        // reset history to this imported baseline
        const baseline = snapshotOf(newNodes, newEdges);
        lastSnapshotRef.current = baseline;
        historyRef.current = [];
        redoRef.current = [];
        setHistCount(0);
        setRedoCount(0);

        // log the rebuilt survey for confirmation
        try {
            const surveyJSON = buildSurveyJSON(newNodes, newEdges);
            // eslint-disable-next-line no-console
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Error building survey JSON after import:', e);
        }
    }, [onChangeNodeData, setNodes, setEdges, buildSurveyJSON, snapshotOf]);

    // Prevent deleting the last remaining node
    const onNodesChangeSafe = useCallback((changes) => {
        const filtered = nodes.length <= 1 ? changes.filter((c) => c.type !== 'remove') : changes;
        onNodesChange(filtered);
    }, [nodes.length, onNodesChange]);

    // Import when survey.content changes (stringified JSON or array)
    useEffect(() => {
        if (!survey || survey.content == null) return;
        let arr = survey.content;
        try {
            if (typeof arr === 'string') arr = JSON.parse(arr);
            importSurvey(arr);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Invalid survey.content JSON', e);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [survey?.content]);

    // Notify parent on any change in graph
    useEffect(() => {
        if (!onChange) return;
        try {
            const json = buildSurveyJSON(nodes, edges);
            onChange(json);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Error building survey JSON for onChange:', e);
        }
    }, [nodes, edges, onChange, buildSurveyJSON]);

    // Track history on graph changes
    useEffect(() => {
        if (applyingHistoryRef.current) {
            // skip recording while applying undo/redo
            applyingHistoryRef.current = false;
            return;
        }
        const snap = snapshotOf(nodes, edges);
        if (!lastSnapshotRef.current) {
            lastSnapshotRef.current = snap;
            return;
        }
        if (!snapshotsEqual(lastSnapshotRef.current, snap)) {
            // push previous to history and clear redo
            historyRef.current.push(lastSnapshotRef.current);
            setHistCount(historyRef.current.length);
            redoRef.current = [];
            setRedoCount(0);
            lastSnapshotRef.current = snap;
        }
    }, [nodes, edges, snapshotOf, snapshotsEqual]);

    const applySnapshot = useCallback((snap) => {
        applyingHistoryRef.current = true;
        setNodes(snap.nodes);
        setEdges(snap.edges);
    }, [setNodes, setEdges]);

    const undo = useCallback(() => {
        if (historyRef.current.length === 0 || !lastSnapshotRef.current) return;
        const prev = historyRef.current.pop();
        const current = lastSnapshotRef.current;
        redoRef.current.push(current);
        setHistCount(historyRef.current.length);
        setRedoCount(redoRef.current.length);
        lastSnapshotRef.current = prev;
        applySnapshot(prev);
    }, [applySnapshot]);

    const redo = useCallback(() => {
        if (redoRef.current.length === 0 || !lastSnapshotRef.current) return;
        const next = redoRef.current.pop();
        historyRef.current.push(lastSnapshotRef.current);
        setHistCount(historyRef.current.length);
        setRedoCount(redoRef.current.length);
        lastSnapshotRef.current = next;
        applySnapshot(next);
    }, [applySnapshot]);

    // Ctrl/Cmd + wheel => vertical pan instead of zoom. Capture at window to prevent default zoom.
    useEffect(() => {
        const el = reactFlowWrapper.current;
        if (!el) return;
        const onWheelCapture = (e) => {
            if (!(e.ctrlKey || e.metaKey)) return;
            if (!el.contains(e.target)) return;
            e.preventDefault();
            e.stopPropagation();
            const vp = getViewport?.();
            if (!vp) return;
            const { x, y, zoom } = vp;
            // scale pan by inverse zoom to keep a consistent perceived speed
            const factor = 1 / (zoom || 1);
            setViewport?.({ x, y: y - e.deltaY * factor, zoom });
        };
        window.addEventListener('wheel', onWheelCapture, { passive: false, capture: true });
        return () => window.removeEventListener('wheel', onWheelCapture, { capture: true });
    }, [getViewport, setViewport]);

    // Keyboard shortcuts: Ctrl/Cmd+Z (undo), Ctrl/Cmd+Shift+Z or Ctrl/Cmd+Y (redo)
    useEffect(() => {
        const handler = (e) => {
            const isModifier = e.ctrlKey || e.metaKey;
            if (!isModifier) return;
            // avoid when editing text inputs or contentEditable
            const t = e.target;
            const tag = (t && t.tagName) || '';
            const isTextInput = t?.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
            if (isTextInput) return;

            const key = String(e.key || '').toLowerCase();
            if (key === 'z' && !e.shiftKey) {
                e.preventDefault();
                undo();
            } else if ((key === 'z' && e.shiftKey) || key === 'y') {
                e.preventDefault();
                redo();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [undo, redo]);

    const onConnectEnd = useCallback(
        (event, connectionState) => {
            // when a connection is dropped on the pane it's not valid
            if (!connectionState.isValid) {
                // we need to remove the wrapper bounds, in order to get the correct position
                const id = getId();
                const { clientX, clientY } =
                    'changedTouches' in event ? event.changedTouches[0] : event;
                // Always use integer notation: Domanda N where N is next stable question id
                const newQid = getNextQuestionIdFromNodes(nodes);
                const newTitle = `Domanda ${newQid}`;
                const srcNode = nodes.find((n) => n.id === connectionState.fromNode?.id);
                const newNode = {
                    id,
                    position: screenToFlowPosition({
                        x: clientX,
                        y: clientY,
                    }),
                    type: 'survey',
                    data: {
                        title: newTitle,
                        onChange: onChangeNodeData,
                        questionType: 'Open',
                        text: '',
                        required: true,
                        group: typeof srcNode?.data?.group === 'string' ? srcNode.data.group : '',
                        qid: newQid,
                        options: [],
                        min: 1,
                        max: 5,
                        step: 1,
                        minLabel: '',
                        maxLabel: '',
                        maxSelections: null,
                        minSelections: null,
                        validation: '',
                    },
                    origin: [0.5, 0.0],
                };

                const label = (() => {
                    const handleId = connectionState.fromHandle?.id;
                    if (srcNode?.data?.options?.length && handleId) {
                        const opt = srcNode.data.options.find((o) => `opt-${o.id}` === handleId);
                        return opt?.value ?? 'next';
                    }
                    return 'next';
                })();

                const newNodesArr = nodes.concat(newNode);
                const newEdgesArr = edges.concat({
                    id: getId(),
                    source: connectionState.fromNode.id,
                    sourceHandle: connectionState.fromHandle?.id,
                    target: id,
                    label,
                });

                setNodes(newNodesArr);
                setEdges(newEdgesArr);

                try {
                    const surveyJSON = buildSurveyJSON(newNodesArr, newEdgesArr);
                    // eslint-disable-next-line no-console
                    // Optional: pretty
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error('Error building survey JSON:', e);
                }
            }
        },
        [screenToFlowPosition, onChangeNodeData, setNodes, setEdges, nodes, edges, buildSurveyJSON, getNextQuestionIdFromNodes],
    );

    const focusGroupNode = useCallback((nodeId) => {
        const target = nodes.find((n) => n.id === nodeId);
        if (!target) return;
        const viewport = getViewport?.();
        const zoom = viewport?.zoom ?? 1;
        if (typeof setCenter === 'function') {
            setCenter(target.position.x, target.position.y + 120, { zoom });
            return;
        }
        if (typeof setViewport === 'function') {
            const bounds = reactFlowWrapper.current?.getBoundingClientRect();
            const width = bounds?.width ?? 0;
            const height = bounds?.height ?? 0;
            const x = (width / 2) - (target.position.x * zoom);
            const y = (height / 2) - ((target.position.y + 120) * zoom);
            setViewport({ x, y, zoom });
        }
    }, [nodes, getViewport, setCenter, setViewport]);

    // ensure initial nodes receive the onChange handler
    useEffect(() => {
        setNodes((prev) =>
            prev.map((n) =>
                n.type === 'survey' && (!n.data || !n.data.onChange)
                    ? { ...n, data: { ...(n.data || {}), onChange: onChangeNodeData } }
                    : n
            )
        );
    }, [onChangeNodeData, setNodes]);

    return (
        <div className={styles.wrapper} ref={reactFlowWrapper}>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChangeSafe}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onConnectEnd={onConnectEnd}
                nodeTypes={nodeTypes}
                fitView
                fitViewOptions={{ padding: 2 }}
                nodeOrigin={nodeOrigin}
                zoomOnPinch={false}
                zoomOnScroll
            >
                <Background />
                <Controls />
                {sectionEntries.length > 0 && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 6,
                            padding: '8px 10px',
                            background: 'rgba(255, 255, 255, 0.92)',
                            borderRadius: 8,
                            boxShadow: '0 6px 18px rgba(15, 23, 42, 0.15)',
                            border: '1px solid #E5E7EB',
                            maxWidth: 220,
                            zIndex: 6,
                        }}
                    >
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#1F2937' }}>Sezioni</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {sectionEntries.map((entry) => (
                                <button
                                    key={entry.key}
                                    type="button"
                                    onClick={() => focusGroupNode(entry.nodeId)}
                                    style={{
                                        textAlign: 'left',
                                        fontSize: 12,
                                        color: '#111827',
                                        background: '#F9FAFB',
                                        border: '1px solid #E5E7EB',
                                        borderRadius: 6,
                                        padding: '6px 8px',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {entry.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {/* Undo / Redo buttons overlay */}
                <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 8, zIndex: 5 }}>
                    <button
                        onClick={undo}
                        disabled={histCount === 0}
                        style={{
                            border: '1px solid #E5E7EB',
                            background: histCount === 0 ? '#F3F4F6' : '#FFFFFF',
                            color: histCount === 0 ? '#9CA3AF' : '#111827',
                            borderRadius: 6,
                            padding: '6px 10px',
                            fontSize: 12,
                            cursor: histCount === 0 ? 'not-allowed' : 'pointer',
                        }}
                        title="Annulla (undo)"
                    >
                        Undo
                    </button>
                    <button
                        onClick={redo}
                        disabled={redoCount === 0}
                        style={{
                            border: '1px solid #E5E7EB',
                            background: redoCount === 0 ? '#F3F4F6' : '#FFFFFF',
                            color: redoCount === 0 ? '#9CA3AF' : '#111827',
                            borderRadius: 6,
                            padding: '6px 10px',
                            fontSize: 12,
                            cursor: redoCount === 0 ? 'not-allowed' : 'pointer',
                        }}
                        title="Ripeti (redo)"
                    >
                        Redo
                    </button>
                </div>

            </ReactFlow>
        </div>
    );
};

export default SurveyEditor